/*
===============================================================================================================
                                 Copyright 2022-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-console */
import apiClient from '@/utilities/api-client';
import { isEmpty } from '@/utilities/validation';
import { merge } from 'lodash';
import compileChangeObject from './compile-change-object';
import { enableCarrierInSingleCarrierMode } from './company-carrier';

const createHeaders = () => ({
  'cache-control': 'no-cache',
});

export const updateWarehouseReq = ({
  locationId, rootOrgId, merchantId,
  body, multiCarrierAddressesApiEnabled,
}) => apiClient.patch(
  `${rootOrgId}/company/${merchantId}${multiCarrierAddressesApiEnabled ? '/multicarrier' : ''}/warehouse/${locationId}`,
  body,
  {
    headers: createHeaders(),
  },
);

export const createWarehouseReq = ({
  rootOrgId, merchantId, body,
  multiCarrierAddressesApiEnabled,
}) => apiClient.post(
  `${rootOrgId}/company/${merchantId}${multiCarrierAddressesApiEnabled ? '/multicarrier' : ''}/warehouse`,
  body,
  {
    headers: createHeaders(),
  },
);

export const deleteWarehouseReq = ({
  rootOrgId, merchantId, locationId, newDefaultAddressId,
}) => {
  let url = `${rootOrgId}/company/${merchantId}/warehouse/${locationId}`;
  if (newDefaultAddressId) url += `?newDefault=${newDefaultAddressId}`;
  return apiClient.delete(
    url,
    {},
    {
      headers: createHeaders(),
    },
  );
};

export const updateDefaultWarehouse = ({
  rootOrgId, merchantId, locationId, body,
}) => apiClient.patch(
  `${rootOrgId}/company/${merchantId}/warehouse/${locationId}/default`,
  body,
  {
    headers: createHeaders(),
  },
);

export default async ({
  changes: changeArray,
  merchantId,
  token,
  rootOrgId,
  warehouseAddress: existingAddress,
  newAddressEnabledByDefault,
  homeCountryCode,
  homeCountryCodeSetToWarehouse,
  getFeature,
  getConfigValue,
  productName,
}) => {
  if (isEmpty(changeArray)) return;

  const changes = compileChangeObject(changeArray);

  const addressChanges = changes?.place?.address ?? {};
  const fieldsToMerge = changes?.mergedFields;
  const changesPresent = changes?.place?.address || fieldsToMerge;

  if (!changesPresent) return;

  const { multiCarrierAddressesApiEnabled } = getFeature('addresses');

  if (fieldsToMerge) {
    const addressPath = existingAddress?.place?.address;
    const existingAddressMap = {
      line3: addressPath?.line3,
      unitNumber: addressPath?.unitNumber,
      blockNumber: addressPath?.blockNumber,
    };

    const mergedInitialFields = merge(existingAddressMap, fieldsToMerge);
    const specialAddressFields = Object.values(mergedInitialFields);
    const mergedFields = specialAddressFields.filter(Boolean).join(',');

    if (!isEmpty(specialAddressFields)) {
      addressChanges.line2 = mergedFields?.length ? mergedFields : null;
    }
  }

  const body = {
    ...(!existingAddress && { companyId: merchantId }),
    ...(!existingAddress && { locationName: 'Main Address' }),
    enabled: multiCarrierAddressesApiEnabled ? undefined : newAddressEnabledByDefault ?? true,
    place: {
      address: {
        ...existingAddress?.place.address,
        ...addressChanges,
        ...(fieldsToMerge && { ...fieldsToMerge }),
        ...(homeCountryCode && homeCountryCodeSetToWarehouse && { country: homeCountryCode }),
      },
    },
    ...(!isEmpty(changes.externalBindings) && { externalBindings: changes.externalBindings }),
  };

  // try to create a warehouse if there isn't one, and set it as default
  if (!existingAddress) {
    try {
      const newWarehouse = await createWarehouseReq({
        rootOrgId, merchantId, body, multiCarrierAddressesApiEnabled,
      });
      // multi-carrier api handles setting the default warehouse
      if (multiCarrierAddressesApiEnabled) return;

      const locationId = newWarehouse.data?.resource?.locationId;

      try {
        await updateDefaultWarehouse({
          rootOrgId, merchantId, locationId, body,
        });
      } catch (e) {
        // if setting a default failed - need to clean up and throw error
        await deleteWarehouseReq({
          rootOrgId, merchantId, locationId,
        });
        console.log('Could not create a warehouse');
        console.log(e);
      }
    } catch (e) {
      console.log('Could not create a warehouse');
      console.log(e);
      throw new Error(e);
    }
  } else {
    await updateWarehouseReq({
      locationId: existingAddress.locationId,
      rootOrgId,
      merchantId,
      body,
      multiCarrierAddressesApiEnabled,
    });
  }
  // check if there's a pending carrier (single carrier mode) that needs to be activated
  const { carriersWithPendingStatus = [] } = getFeature('carriers') || {};
  const merchantCarriers = getConfigValue('carriersConfig');
  const pendingCarrier = merchantCarriers?.find((carrier) => carriersWithPendingStatus.includes(carrier.carrierCompanyId));

  if (pendingCarrier && !isEmpty(changes.externalBindings)) {
    await enableCarrierInSingleCarrierMode({
      merchantId,
      getConfigValue,
      carrierCompanyId: pendingCarrier.carrierCompanyId,
      rootOrgId,
      token,
      productName,
    });
  }
};
